/* eslint-disable @typescript-eslint/naming-convention */
import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import { Badge, Button, Heading, Text } from '@apollo/orbit';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { Loading } from 'src/components/common/loading/Loading';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ampli } from 'src/lib/analytics/amplitude/vendor';

import { DeploymentOptionCard } from '../components/DeploymentOptionCard';
import {
  getCloudProviderOptions,
  getQuickDeploymentOptions,
} from '../config/platforms';

/**
 * Page for deploying a graph variant when users don't have an existing endpoint.
 * Provides various deployment options including cloud providers and local development.
 */
export const DeploymentFlowPage = () => {
  const { graphId, variantName } = useRouteParams(orgRoutes.deploymentFlow);
  const [accountId] = useCurrentAccountId();
  const history = useHistory();

  // Track page view
  useEffect(() => {
    if (graphId && variantName) {
      ampli.deploymentFlowPageViewed({
        graph_ref: `${graphId}@${variantName}`,
      });
    }
  }, [graphId, variantName]);

  // Validate route parameters - if invalid, show error instead of infinite loading
  if (!graphId || !variantName) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Heading as="h1" size="xl" className="mb-4">
            Invalid Graph or Variant
          </Heading>
          <Text className="mb-6">
            The graph ID or variant name in the URL is invalid.
          </Text>
          <Button
            variant="primary"
            onClick={() =>
              history.push(orgRoutes.graphs.path({ orgId: accountId || '' }))
            }
          >
            Back to Graphs
          </Button>
        </div>
      </div>
    );
  }

  // Only show loading for temporary accountId absence
  if (!accountId) {
    return <Loading />;
  }

  // Get deployment options from centralized platform configuration
  const quickDeploymentOptions = getQuickDeploymentOptions().map((option) => ({
    ...option,
    onClick: () => {
      ampli.deploymentPlatformClicked({
        graph_ref: `${graphId}@${variantName}`,
        deployment_platform: option.id,
      });
      history.push(
        orgRoutes.deploymentInstructions.path({
          orgId: accountId,
          graphId,
          variantName,
          platform: option.id,
        }),
      );
    },
  }));

  const cloudProviderOptions = getCloudProviderOptions();

  return (
    <div className="h-screen overflow-auto bg-primary">
      <div className="mx-auto max-w-screen-xl p-8">
        <Button
          variant="hidden"
          size="md"
          leftIcon={<IconArrowLeft />}
          onClick={() =>
            history.push(orgRoutes.graphs.path({ orgId: accountId }))
          }
          className="mb-8 p-0"
        >
          Back to all graphs
        </Button>

        {/* Page header */}
        <div className="mb-12">
          <Heading as="h1" size="3xl" className="mb-4">
            Get an endpoint for {graphId}@{variantName}
          </Heading>
          <Text variant="subtitle" className="text-primary">
            Deploy the Apollo Router to obtain a live, reachable endpoint for
            your graph.
          </Text>
        </div>

        {/* Quick and simple section */}
        <section className="mb-14">
          <div className="mb-4">
            <div className="mb-2 flex items-center gap-2">
              <Heading as="h2" variant="heading" size="xl">
                Quick and simple templates
              </Heading>
              <Badge variant="info" size="sm">
                recommended
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {quickDeploymentOptions.map((option) => (
              <DeploymentOptionCard key={option.id} provider={option} />
            ))}
          </div>
        </section>

        {/* Additional deployment guides section */}
        <section className="mb-8">
          <div className="mb-5">
            <Heading as="h2" variant="heading" size="xl" className="mb-2">
              Additional deployment guides
            </Heading>
            <Text variant="body" className="text-secondary">
              Check out our deployment guides for full instructions on how to
              set up a runtime in your cloud environment.
            </Text>
          </div>

          <div className="flex flex-col items-start space-y-2">
            {cloudProviderOptions.map((option) => (
              <div
                key={option.id}
                className="flex w-full items-center justify-between border-b border-primary bg-primary px-0 py-4 md:w-1/2"
              >
                <div className="flex items-center gap-4">
                  <div className="flex size-11 items-center justify-center rounded-lg border border-primary">
                    {option.icon && <option.icon className="size-6" />}
                  </div>
                  <Text className="text-title">{option.name}</Text>
                </div>
                {option.url ? (
                  <Button
                    variant="secondary"
                    size="md"
                    rightIcon={<IconOutlink />}
                    as="a"
                    href={option.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Follow guide
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};
